import { computed, inject, Injectable, signal } from '@angular/core';
import { UserNotificationsState } from './models/userNotificationsState';
import { toSignal } from '@angular/core/rxjs-interop';
import {
	catchError,
	filter,
	map,
	merge,
	Observable,
	of,
	Subject,
	switchMap,
	tap,
	timer,
} from 'rxjs';
import { NotificationsService } from '../notifications/notifications.service';
import { NotificationReadsService } from '../notification-reads/notification-reads.service';
import { UserStoreService } from '@user-store';
import { App, APP_NAME, Notification, NotificationReads } from '@agilox/common';

@Injectable({
	providedIn: 'root',
})
export class UserNotificationsService {
	private notificationsService: NotificationsService = inject(NotificationsService);
	private notificationReadsService: NotificationReadsService = inject(NotificationReadsService);
	private userStoreService: UserStoreService = inject(UserStoreService);
	private appName = inject<App>(APP_NAME, { optional: true });

	state = computed<UserNotificationsState>(() => ({
		notifications: this.notifications(),
		hasUnreadNotifications: this.notifications().some((n) => !n.read),
		isOpen: this._isOpen(),
		isInitialLoading: this._isInitialLoading(),
	}));

	unreadCount = computed(() => this.notifications().filter((n) => !n.read).length);

	private readonly _isOpen = signal(false);
	private readonly _isInitialLoading = signal(true);
	private readonly _notificationsUpdate$ = new Subject<Notification[]>();

	private interval = 180000;

	private readonly notifications$: Observable<Notification[]> = timer(0, this.interval).pipe(
		filter(() => document.visibilityState === 'visible'),
		switchMap(() =>
			this.notificationsService
				.getNotifications(this.appName ?? App.analytics, 1, 5, '-created_at', true)
				.pipe(
					map((response) => response.notifications),
					switchMap((notifications) => this.mergeReadStatuses(notifications)),
					catchError(() => {
						this._isInitialLoading.set(false);
						return of(this.notifications());
					})
				)
		),
		tap(() => this._isInitialLoading.set(false))
	);

	private readonly notifications = toSignal(
		merge(this.notifications$, this._notificationsUpdate$),
		{ initialValue: [] }
	);

	private mergeReadStatuses(notifications: Notification[]) {
		const notificationIds = notifications.map((n) => n.id);
		const readNotificationsMap = new Map(
			this.notifications().map((notification) => [notification.id, notification.read ?? false])
		);

		if (notificationIds.length === 0) {
			return of([]);
		}

		return this.notificationReadsService
			.getNotificationReads(
				1,
				notificationIds.length,
				'-created_at',
				this.userStoreService.currentUser()?.id,
				notificationIds
			)
			.pipe(
				map(({ notificationReads }) => {
					const readIds = new Set(notificationReads.map((n) => n.notificationId));

					return notifications.map((notification) => ({
						...notification,
						read:
							readIds.has(notification.id) || readNotificationsMap.get(notification.id) === true,
					}));
				})
			);
	}

	toggleNotificationsOffcanvas(): void {
		this._isOpen.update((v) => !v);
	}

	setStateToRead(): void {
		const updated = this.notifications().map((notification: Notification) => ({
			...notification,
			read: true,
		}));
		this._notificationsUpdate$.next(updated);
	}
}
